import {
  Component,
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { times } from 'lodash'
import parse from 'html-react-parser'
import { DateTime } from 'luxon'

import { PayrollSetupList } from './PayrollSetupList'
import {
  fetchEmployees,
  fetchEmployeeStateTaxes,
  putUpdateEmployeeStateTaxes,
  PUT_UPDATE_EMPLOYEE_STATE_TAXES_KEY,
} from '../payrollActions'
import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import {
  GEP_ENROLL_PATHS,
  ONBOARDING_STEPS,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import { PayrollError } from '../PayrollError'
import {
  Button,
  Card,
  DatePicker,
  Select,
  Input,
  Separator,
  Text,
  Label,
  CardColors,
} from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { selectFirstEmployee } from '../payroll.selectors'
import { getIsFetching } from '@/reducers/fetch'
import {
  Gusto_EmployeeStateTaxes,
  Gusto_EmployeeStateTaxesInput,
  Gusto_EmployeeStateTaxesQuestion,
} from '../generated_gusto_types'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { shortNames } from '@/constants/statesShortNamesConstants'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TextColors, TypographyStyles } from '@/styles'
import CurrencyInput from 'react-currency-input-field'
// The employee state question api is a bit unusual
// 1. We fetch the questions `fetchEmployeeStateTaxes` (payload `EmployeeStateTaxes`).  This payload will be different per US state
// 2. We render the questions from that response within this component based on that payload
// 3. Once this page is submitted `getAnswer` is called on each question.  `StateTaxInput` will send the exact question
// payload it was given but with the answer included
// 4. Those are posted via `putUpdateEmployeeStateTaxes`

interface TaxProps {
  question: Gusto_EmployeeStateTaxesQuestion
}

interface TaxState {
  answer: string | boolean | number | undefined
}

class StateTaxInput extends Component<TaxProps, TaxState> {
  constructor(props: TaxProps) {
    super(props)

    this.state = {
      answer: this.props.question.answers[0]?.value,
    }
  }

  getAnswer() {
    if (this.state.answer === undefined) {
      return null
    }

    return {
      key: this.props.question.key,
      answers: [
        {
          value: this.state.answer,
          valid_from: '2010-01-01',
          valid_up_to: null,
        },
      ],
    }
  }

  renderFormInput() {
    const { question } = this.props

    const description = question.description
      ?.replaceAll('#{year}', DateTime.now().year.toString())
      ?.replaceAll('#{nextYear}', (DateTime.now().year + 1).toString())

    const formatType = question.input_question_format.type

    const inputProps = {
      fullWidth: true,
      label: question.label,
      description: description && parse(description),
    }

    if (formatType === 'Select') {
      return (
        <>
          <Label>{question.label}</Label>
          <Text
            variant={TypographyStyles.BODY_XS}
            className="my-2"
            color={TextColors.MUTED}
          >
            {description && parse(description)}
          </Text>
          <Select
            options={question.input_question_format.options?.map((option) => ({
              text: option.label || '',
              value: option.value,
            }))}
            value={this.state.answer}
            onValueChange={(value) => this.setState({ answer: value })}
            {...inputProps}
          />
        </>
      )
    } else if (formatType === 'Date') {
      return (
        <>
          <Label>{question.label}</Label>
          <Text
            variant={TypographyStyles.BODY_XS}
            className="my-2"
            color={TextColors.MUTED}
          >
            {description && parse(description)}
          </Text>
          <DatePicker
            value={(this.state.answer || '').toString()}
            onChange={(value) => this.setState({ answer: value })}
            dateFormat={DATE_FORMATS_LUXON.GUSTO_SUBMIT}
            {...inputProps}
          />
        </>
      )
    } else if (formatType === 'Currency') {
      return (
        <>
          <Label>{question.label}</Label>
          <Text
            variant={TypographyStyles.BODY_XS}
            className="my-2"
            color={TextColors.MUTED}
          >
            {description && parse(description)}
          </Text>
          <CurrencyInput
            prefix="$"
            value={(this.state.answer === undefined
              ? ''
              : this.state.answer
            ).toString()}
            onValueChange={(_, __, values) => {
              // Currency input can return `09` as a value.  The 0 needs to be removed however for `0.` the 0 cannot be removed
              const cleaned =
                values?.value === '0.'
                  ? values?.value
                  : values?.value?.replace(/^0+/, '') || '0'

              this.setState({ answer: cleaned })
            }}
          />
        </>
      )
    } else {
      return (
        <>
          <Label>{question.label}</Label>
          <Text
            variant={TypographyStyles.BODY_XS}
            className="my-2"
            color={TextColors.MUTED}
          >
            {description && parse(description)}
          </Text>
          <Input
            value={(this.state.answer === undefined
              ? ''
              : this.state.answer
            ).toString()}
            onChange={(e) => this.setState({ answer: e.target.value })}
            {...inputProps}
          />
        </>
      )
    }
  }

  render() {
    return <div>{this.renderFormInput()}</div>
  }
}

export type StateTaxSectionHandle = {
  getQuestionAnswers: () => Gusto_EmployeeStateTaxesInput['states'][number]
}
export const StateTaxSection = forwardRef<
  StateTaxSectionHandle,
  { taxesForSingleState: Gusto_EmployeeStateTaxes }
>(({ taxesForSingleState }, ref) => {
  const questionRefs = useMemo(
    () =>
      times(taxesForSingleState.questions.length).map(() =>
        createRef<StateTaxInput>()
      ),
    [taxesForSingleState.questions.length]
  )

  const getQuestionAnswers = useCallback(
    () => ({
      state: taxesForSingleState.state,
      questions: filterNulls(
        questionRefs.map((ref) => ref.current?.getAnswer())
      ),
    }),
    [questionRefs, taxesForSingleState.state]
  )

  useImperativeHandle(ref, () => ({ getQuestionAnswers }))

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="mb-3">
        <Text variant={TypographyStyles.DISPLAY_XXS}>
          {shortNames[taxesForSingleState.state] || taxesForSingleState.state}
        </Text>
      </div>
      {taxesForSingleState.questions.map((question, index) => {
        if (!questionRefs[index]) return null

        return (
          <div className="mb-4" key={question.key}>
            <StateTaxInput question={question} ref={questionRefs[index]} />
          </div>
        )
      })}
    </Card>
  )
})

const StateTaxForm = ({
  stateTaxes,
}: {
  stateTaxes: Gusto_EmployeeStateTaxes[]
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.employeeStateTaxes)

  const isPosting = useReselector(
    getIsFetching,
    PUT_UPDATE_EMPLOYEE_STATE_TAXES_KEY
  )

  const employeeUuid = stateTaxes[0]?.employee_uuid

  const stateSectionRefs = useMemo(
    () =>
      times(stateTaxes.length).map(() => createRef<StateTaxSectionHandle>()),
    [stateTaxes.length]
  )

  const submit = useCallback(async () => {
    const payload = {
      states: filterNulls(
        stateSectionRefs.map((section) => section.current?.getQuestionAnswers())
      ),
    }

    const res = await putUpdateEmployeeStateTaxes(
      employeeUuid,
      payload
    )(dispatch)

    if (res) {
      track('completed payroll step - employee state taxes')
      navigate(GEP_ENROLL_PATHS.employeeBank)
    }
  }, [stateSectionRefs, employeeUuid, dispatch, track, navigate])

  return (
    <div className="flex flex-col gap-4">
      <Text variant={TypographyStyles.H2} className="mt-4">
        Add yourself as an employee
      </Text>

      <Separator />

      <Text variant={TypographyStyles.H2}>
        Enter Employee State Tax Information
      </Text>

      <PayrollError fetchKey={PUT_UPDATE_EMPLOYEE_STATE_TAXES_KEY} />

      {stateTaxes.map((taxesForSingleState, index) => {
        if (!stateSectionRefs[index]) return null

        return (
          <StateTaxSection
            key={taxesForSingleState.state}
            ref={stateSectionRefs[index]}
            taxesForSingleState={taxesForSingleState}
          />
        )
      })}

      <div className="flex flex-wrap gap-4 justify-between">
        <div className="w-full md:w-1/2">
          <Button
            variant="secondary"
            onClick={() => navigate(GEP_ENROLL_PATHS.employeeFederal)}
          >
            Back
          </Button>
        </div>
        <div className="w-full md:w-1/2">
          <Button onClick={submit} loading={isPosting}>
            Save & Continue
          </Button>
        </div>
      </div>
    </div>
  )
}

export const PayrollEmployeeState = () => {
  const dispatch = useAppDispatch()
  const [stateTaxes, setStateTaxes] = useState<Gusto_EmployeeStateTaxes[]>()
  const firstEmployee = useReselector(selectFirstEmployee)

  useEffect(() => {
    dispatch(fetchEmployees())
  }, [dispatch])

  useEffect(() => {
    const fetch = async () => {
      if (!firstEmployee?.uuid) {
        return
      }

      const stateTaxRes = await fetchEmployeeStateTaxes(firstEmployee?.uuid)(
        dispatch
      )

      if (stateTaxRes?.length) {
        setStateTaxes(stateTaxRes)
      }
    }

    fetch()
  }, [dispatch, firstEmployee?.uuid])

  return (
    <div>
      <Card>
        <div className="flex flex-col gap-4">
          <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>

          <Separator />

          <PrefilledNoticeContainer />

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="w-full lg:w-2/3">
              {stateTaxes && <StateTaxForm stateTaxes={stateTaxes} />}
            </div>
            <div className="w-full lg:w-1/3">
              <PayrollSetupList />
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
